import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Voice Agent step — makes an outbound phone call via Vapi and returns
 * transcript, recording, and post-call AI analysis.
 *
 * The assistant (model, voice, analysis plan, etc.) is configured in the
 * Vapi dashboard. This step references a voice agent configuration that
 * bundles an assistant and phone number.
 *
 * Requires `VAPI_API_KEY` in the environment.
 *
 * @see backend/src/workflows/steps/ai/README.md
 */
export const voiceAgentStep = defineStepConfig({
  type: "voice_agent",
  name: "Voice Agent",
  description:
    "Makes an outbound phone call using an AI voice agent and returns the call transcript, analysis, and recording",
  category: "AI & Agents",

  inputSchema: z.object({
    phoneNumber: z
      .string()
      .describe("Phone number to call (E.164 format, e.g. +14155551234)"),
    firstMessage: z
      .string()
      .optional()
      .describe(
        "Optional first message the agent says when the call connects. Overrides the default greeting configured on the assistant.",
      ),
  }),

  // Loose, not a plain object: when an extraction schema is configured the
  // handler flattens Vapi's structured data onto the result, one key per field,
  // and `generateVoiceAgentOutputSockets` publishes a socket for each. Output
  // validation `.parse()`s the handler's return value, and a plain z.object
  // strips those keys — leaving every extraction socket empty at runtime.
  outputSchema: z.looseObject({
    transcript: z.string().optional().describe("Full call transcript"),
    summary: z.string().optional().describe("AI-generated call summary"),
  }),

  configSchema: z.object({
    voiceAgentConfigId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "ID of the voice agent configuration to use for this call. Optional at save time to allow draft steps, but required at execution — the handler will throw if missing.",
      ),

    extractionSchemaDefinitionId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "Optional extraction schema. When set, the schema fields become individual output sockets and Vapi's analysis plan is configured to extract them.",
      ),

    systemPrompt: z
      .string()
      .optional()
      .describe(
        "Optional system prompt override. Replaces the prompt configured on the assistant for this call.",
      ),

    voicemailMessage: z
      .string()
      .optional()
      .describe(
        "Message the agent leaves if the call goes to voicemail. Overrides the default from the voice agent configuration.",
      ),

    maxWaitTimeMs: z
      .number()
      .default(1_800_000)
      .describe("Max wait for call completion in ms (default: 30 min)"),
    pollIntervalMs: z
      .number()
      .default(5_000)
      .describe("Initial poll interval in ms (default: 5s)"),
    maxPollDelayMs: z
      .number()
      .default(15_000)
      .describe("Max poll interval with backoff in ms (default: 15s)"),
    backoffMultiplier: z
      .number()
      .default(1.5)
      .describe("Multiplier for polling backoff"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: {
      maximumAttempts: 1,
    },
  },
});
